import { useMemo, useState } from "react";
import type { PaginationProps } from "@/components/ui/pagination";

/** Tamanho de página padrão das listagens do painel. */
export const DEFAULT_PAGE_SIZE = 25;

export interface ClientPagination<T> {
  /** Os itens da página atual. */
  pageItems: T[];
  /** Propriedades prontas para o componente `Pagination`. */
  paginationProps: Omit<PaginationProps, "itemLabel" | "className" | "loading">;
  /** Volta para a primeira página (por exemplo, depois de remover um item). */
  reset: () => void;
}

/**
 * Paginação em memória, para as listas que precisam da coleção inteira.
 *
 * Vendedores, clientes, cupons, categorias e faturas são carregados inteiros de
 * propósito: a busca sem acento, o filtro e a ordenação acontecem aqui, e o
 * Firestore omitiria do resultado o documento a que falta o campo de ordenação
 * (ver docs/06, seção 6.2). O que esta paginação poupa é o desenho: a tabela
 * mostra uma página por vez, e uma lista de milhares de linhas deixa de travar
 * o navegador.
 *
 * `resetKey` deve mudar junto com a busca e os filtros: uma busca nova que
 * começasse na página 7 mostraria uma tela vazia sem motivo aparente.
 */
export function usePagination<T>(
  items: T[],
  resetKey = "",
  pageSize = DEFAULT_PAGE_SIZE,
): ClientPagination<T> {
  const [state, setState] = useState<{ page: number; key: string }>({ page: 1, key: resetKey });

  // Reinicia na primeira página quando o filtro muda, sem efeito: comparar a
  // chave durante o render evita um quadro desenhado com a página antiga.
  const currentPage = Object.is(state.key, resetKey) ? state.page : 1;
  if (!Object.is(state.key, resetKey)) {
    setState({ page: 1, key: resetKey });
  }

  const total = items.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  // Remover o último item da última página não pode deixar a tela numa página
  // que não existe mais.
  const page = Math.min(currentPage, pageCount);

  const pageItems = useMemo(
    () => items.slice((page - 1) * pageSize, page * pageSize),
    [items, page, pageSize],
  );

  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return {
    pageItems,
    reset: () => setState({ page: 1, key: resetKey }),
    paginationProps: {
      page,
      pageCount,
      total,
      rangeStart,
      rangeEnd,
      hasPrevious: page > 1,
      hasNext: page < pageCount,
      onPrevious: () => setState({ page: Math.max(1, page - 1), key: resetKey }),
      onNext: () => setState({ page: Math.min(pageCount, page + 1), key: resetKey }),
    },
  };
}
